import type {
	Box,
	RestingPlace,
	StoredFabPosition,
	Viewport,
} from "./fab-drag";
import type { Locale } from "./i18n";
import type { LauncherRect } from "./launcher-anchor";
import type { FabConfig, FabMode, NotificationsConfig } from "./types";

import {
	clampAlong,
	parseStoredPosition,
	reflowRestingPlace,
	resolveRestingPlace,
	restingPlaceFromStored,
	toStoredPosition,
} from "./fab-drag";
import fabCss from "./fab.css?inline";
import { svgFrom } from "./glyphs";
import { setLanguage, t, tWith } from "./i18n";
import { closeSvg, logoSvg } from "./logo";
import { attachStyles } from "./styles";

export interface FabBadgeState {
	// Unread conversations, not messages – three replies in one thread is 1.
	readonly count: number;
	readonly mode: NotificationsConfig["badge"];
}

export interface FabHandle {
	/**
	 * The edge the launcher currently sits on. The configured corner until a
	 * chat-head drag parks the launcher on the other side (or, for the
	 * horizontal tab, in the other half of the bottom edge). The panel docks
	 * to this edge so it always slides out from the launcher.
	 */
	readonly currentSide: () => "left" | "right";
	readonly dispose: () => void;
	/** Move focus to the launcher button – a no-op while it is hidden. */
	readonly focus: () => void;
	/**
	 * Subscribe to the launcher's moves: `"moving"` once a press turns into a
	 * drag, `"settled"` whenever `restingRect` may have changed – a release,
	 * a position restored from the last page, a viewport resize, or the
	 * launcher being hidden or shown. Returns the unsubscribe.
	 */
	readonly onPlaceChange: (
		listener: (change: PlaceChange) => void,
	) => () => void;
	/**
	 * Where the launcher rests, for the surfaces that pin next to it. Once a
	 * drag (or a restored position) parked it, this is computed from the
	 * target place, so it is right while the release glide still plays.
	 * Before that it is measured: the configured spot depends on the mode and
	 * the margin, so no fixed corner fits every launcher. `undefined` while the
	 * launcher is hidden – its surfaces then fall back to the corner.
	 */
	readonly restingRect: () => LauncherRect | undefined;
	readonly setBadge: (state: FabBadgeState) => void;
	readonly setExpanded: (expanded: boolean) => void;
	/**
	 * Re-label the launcher in another language. The widget reports the
	 * language it settled on for the signed-in user (`weloop:locale`), which
	 * can differ from the host-page guess the FAB booted with.
	 */
	/** `refresh` redraws the labels even when the locale did not change – the
	 *  frame may have handed over new strings for the same locale. */
	readonly setLocale: (locale: Locale, refresh?: boolean) => void;
	/**
	 * Suppress the built-in hover tooltip. Set while the project's onboarding
	 * card owns the hover surface – two tooltips on one bubble would fight.
	 */
	readonly setTooltipSuppressed: (suppressed: boolean) => void;
	readonly setVisible: (visible: boolean) => void;
}

export type PlaceChange = "moving" | "settled";

// Past nine the number stops fitting a 64px bubble's corner and stops
// meaning anything more precise than "several".
const BADGE_CAP = 9;

const HOST_ID = "weloop-fab";

// --- Chat-head drag (floating launcher) ----------------------------------
//
// Messenger-style chat head: press and drag moves the launcher anywhere on
// the page; on release it glides back to its home edge (fab.css animates the
// inline `left`/`top` once `data-dragging` clears) – the nearest side edge
// for the bubble and the vertical tab, the bottom edge for the horizontal
// tab (see fab-drag.ts). A press that moves less than the threshold stays a
// plain click and opens the panel.

const DRAG_THRESHOLD_PX = 6;
const POSITION_STORAGE_KEY = "weloop-fab-position";
const FALLBACK_SIZE_PX = 64;

// sessionStorage can throw on locked-down host pages – the launcher then
// simply starts from its configured corner on every load.
const readStoredPosition = (mode: FabMode): StoredFabPosition | null => {
	try {
		return parseStoredPosition(
			sessionStorage.getItem(POSITION_STORAGE_KEY),
			mode,
		);
	} catch {
		return null;
	}
};

const storePosition = (position: StoredFabPosition): void => {
	try {
		sessionStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
	} catch {
		// Position simply resets on the next page load.
	}
};

const viewport = (): Viewport => ({
	height: window.innerHeight,
	width: window.innerWidth,
});

interface ChatHeadDrag {
	readonly detach: () => void;
	readonly restingRect: () => LauncherRect | undefined;
}

const attachChatHeadDrag = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLElement is an external mutable DOM interface
	host: HTMLElement,
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- HTMLButtonElement is an external mutable DOM interface
	button: HTMLButtonElement,
	mode: FabMode,
	marginPx: number,
	onPlaceChange: (change: PlaceChange) => void,
): ChatHeadDrag => {
	// fab.css keys the snap transition and `touch-action: none` off this flag.
	host.dataset["floating"] = "true";

	// The configured margin is a side offset for the bubble and the horizontal
	// tab, but a top offset for the vertical tab, which sits flush on its edge.
	const sideInsetPx = mode === "vertical-tab" ? 0 : marginPx;

	// A hidden launcher (`display: none`) measures 0×0, so the last size it
	// had on screen is kept for the placements computed while it is hidden.
	let lastSize: Pick<Box, "height" | "width"> = {
		height: FALLBACK_SIZE_PX,
		width: FALLBACK_SIZE_PX,
	};
	const size = (): Pick<Box, "height" | "width"> => {
		if (host.offsetWidth > 0 && host.offsetHeight > 0) {
			lastSize = { height: host.offsetHeight, width: host.offsetWidth };
		}
		return lastSize;
	};
	const currentBox = (): Box => {
		const rect = host.getBoundingClientRect();
		return {
			height: rect.height || FALLBACK_SIZE_PX,
			left: rect.left,
			top: rect.top,
			width: rect.width || FALLBACK_SIZE_PX,
		};
	};

	// While floating, the host is always positioned through inline `left` +
	// `top` (never `right`/`bottom`), so the snap glide is a plain CSS
	// transition on two properties instead of a side switch that cannot
	// animate.
	const applyPlace = (place: RestingPlace): void => {
		host.style.left = `${place.left}px`;
		host.style.right = "auto";
		host.style.top = `${place.top}px`;
		host.style.bottom = "auto";
		// Keeps the tooltip and the tab's rounded corners on the inner side,
		// and tells the panel which edge to slide out from.
		host.dataset["position"] = place.side;
	};

	let parkedPlace: RestingPlace | undefined = undefined;
	const settleAt = (place: RestingPlace): void => {
		parkedPlace = place;
		applyPlace(place);
		storePosition(toStoredPosition(mode, place));
		onPlaceChange("settled");
	};

	const stored = readStoredPosition(mode);
	if (stored !== null) {
		settleAt(restingPlaceFromStored(stored, size(), viewport(), sideInsetPx));
	}

	let pointerId: number | null = null;
	let startX = 0;
	let startY = 0;
	let originLeft = 0;
	let originTop = 0;
	let dragged = false;
	let suppressClick = false;

	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- PointerEvent is an external mutable DOM interface
	const onPointerDown = (event: PointerEvent): void => {
		if (event.pointerType === "mouse" && event.button !== 0) {
			return;
		}
		const rect = host.getBoundingClientRect();
		({ pointerId } = event);
		startX = event.clientX;
		startY = event.clientY;
		originLeft = rect.left;
		originTop = rect.top;
		dragged = false;
		suppressClick = false;
		try {
			button.setPointerCapture(event.pointerId);
		} catch {
			// Without capture the drag still works while the pointer stays
			// over the launcher.
		}
	};

	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- PointerEvent is an external mutable DOM interface
	const onPointerMove = (event: PointerEvent): void => {
		if (pointerId === null || event.pointerId !== pointerId) {
			return;
		}
		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;
		if (!dragged) {
			if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) {
				return;
			}
			dragged = true;
			// Kills the transition so the launcher tracks the finger 1:1.
			host.dataset["dragging"] = "true";
			onPlaceChange("moving");
		}
		const { height, width } = size();
		const { height: viewportHeight, width: viewportWidth } = viewport();
		host.style.left = `${clampAlong(originLeft + deltaX, width, viewportWidth)}px`;
		host.style.right = "auto";
		host.style.top = `${clampAlong(originTop + deltaY, height, viewportHeight)}px`;
		host.style.bottom = "auto";
	};

	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- PointerEvent is an external mutable DOM interface
	const onPointerEnd = (event: PointerEvent): void => {
		if (pointerId === null || event.pointerId !== pointerId) {
			return;
		}
		pointerId = null;
		if (!dragged) {
			return;
		}
		// Removing the flag re-enables the transition, so the applyPlace call
		// below plays as the glide to the home edge.
		delete host.dataset["dragging"];
		suppressClick = true;
		settleAt(resolveRestingPlace(mode, currentBox(), viewport(), sideInsetPx));
	};

	// Capture phase, so the post-drag click never reaches the open handler –
	// releasing the launcher must not toggle the panel.
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- MouseEvent is an external mutable DOM interface
	const onClickCapture = (event: MouseEvent): void => {
		if (!suppressClick) {
			return;
		}
		suppressClick = false;
		event.preventDefault();
		event.stopImmediatePropagation();
	};

	// Keeps a parked launcher on its edge (and on-screen) across viewport
	// resizes and orientation changes.
	//
	// Resolved from the remembered place, never from the launcher's rectangle:
	// the launcher is hidden during a screenshot or a recording, and the
	// browser's screen-sharing bar resizes the viewport at that moment. A
	// hidden launcher measures at the top-left corner, which would park it –
	// and dock the panel – on the left edge.
	const onResize = (): void => {
		if (parkedPlace === undefined) {
			return;
		}
		parkedPlace = reflowRestingPlace(
			mode,
			parkedPlace,
			size(),
			viewport(),
			sideInsetPx,
		);
		applyPlace(parkedPlace);
		onPlaceChange("settled");
	};

	button.addEventListener("pointerdown", onPointerDown);
	button.addEventListener("pointermove", onPointerMove);
	button.addEventListener("pointerup", onPointerEnd);
	button.addEventListener("pointercancel", onPointerEnd);
	button.addEventListener("click", onClickCapture, true);
	window.addEventListener("resize", onResize);

	return {
		detach: () => {
			button.removeEventListener("pointerdown", onPointerDown);
			button.removeEventListener("pointermove", onPointerMove);
			button.removeEventListener("pointerup", onPointerEnd);
			button.removeEventListener("pointercancel", onPointerEnd);
			button.removeEventListener("click", onClickCapture, true);
			window.removeEventListener("resize", onResize);
		},
		restingRect: () => {
			if (parkedPlace === undefined) {
				return undefined;
			}
			const { height, width } = size();
			return {
				bottom: parkedPlace.top + height,
				left: parkedPlace.left,
				right: parkedPlace.left + width,
				top: parkedPlace.top,
			};
		},
	};
};

export const mountFab = (
	config: FabConfig,
	locale: Locale,
	onClick: () => void,
	visible: boolean,
	// Hover on the launcher itself. Listened for on the host element, which
	// receives pointer events even though the shadow root is closed – the
	// onboarding card lives in its own root and cannot use a CSS sibling
	// selector the way the built-in tooltip does.
	onHoverChange?: (hovering: boolean) => void,
): FabHandle => {
	const host = document.createElement("div");
	host.id = HOST_ID;
	// Custom properties cross the shadow boundary (they're inherited), so the
	// button inside the shadow root picks up the host page's per-project theme
	// without having to re-thread the values through JS.
	host.style.setProperty("--weloop-fab", config.backgroundColor);
	host.style.setProperty("--weloop-fab-foreground", config.foregroundColor);
	host.style.setProperty("--weloop-fab-margin", `${config.marginPx}px`);
	host.dataset["position"] = config.position;
	// fab.css keys the launcher's shape and anchoring off the mode: corner
	// bubble, side-edge vertical tab, or bottom-edge horizontal tab.
	host.dataset["mode"] = config.mode;
	// Mirror the open state on the host through a data attribute so external
	// observers (host-page code, e2e tests) can read it without piercing the
	// closed shadow root. aria-expanded would be invalid here – the host is a
	// role-less <div> – so the real aria-expanded stays on the inner button,
	// which assistive tech reaches through the closed shadow root. fab.css
	// also keys the logo→close cross-fade off this attribute.
	host.dataset["expanded"] = "false";
	// Visibility is host-controlled (see `setVisible`): a host page that drives
	// the widget from its own UI starts the FAB hidden via `launcher: false`,
	// then can reveal it at runtime. `:host([data-hidden])` in fab.css applies
	// `display: none` so the hidden FAB takes no space and isn't focusable.
	host.dataset["hidden"] = visible ? "false" : "true";
	setLanguage(host, locale);

	// Closed mode: host page scripts can't reach inside via `.shadowRoot` –
	// they only see the wrapper <div>. The snippet keeps the only handle.
	const shadow = host.attachShadow({ mode: "closed" });

	attachStyles(shadow, fabCss);

	let currentLocale = locale;
	// A tab shows its label, so the label leads its accessible name – a voice
	// user says what they see (WCAG 2.5.3). The bubble shows no text.
	const visibleLabel = config.mode === "bubble" ? "" : config.label;
	let labelOpen = visibleLabel || t(locale, "open");
	let labelClose = t(locale, "close");
	let tooltip: HTMLDivElement | undefined;

	const button = document.createElement("button");
	button.className = "fab";
	button.type = "button";
	button.setAttribute("aria-label", labelOpen);
	button.setAttribute("aria-haspopup", "dialog");
	button.setAttribute("aria-expanded", "false");

	// Layered bubble so the open/close swap is a CSS cross-fade instead of an
	// innerHTML replacement: a glow halo behind a white circle that stacks the
	// brand mark and the close glyph. fab.css shows exactly one of the two,
	// keyed off the host's data-expanded attribute.
	const glow = document.createElement("div");
	glow.className = "glow";

	const circle = document.createElement("div");
	circle.className = "circle";

	const logo = document.createElement("div");
	logo.className = "logo";
	if (config.iconImageUrl !== undefined) {
		// Uploaded icon image. Built with createElement + src assignment, so
		// the config value can only ever be a URL, never markup. The parser
		// already restricted it to http(s).
		const image = document.createElement("img");
		image.className = "icon-image";
		image.alt = "";
		image.src = config.iconImageUrl;
		logo.append(image);
	} else if (config.icon === "") {
		// An inline SVG can't ship through the JSON config, so the
		// empty-string icon sentinel selects the bundled Weloop brand mark.
		// The markup is a static build-time asset, never host-page input.
		logo.append(svgFrom(logoSvg));
	} else {
		logo.textContent = config.icon;
	}

	const cross = document.createElement("div");
	cross.className = "cross";
	cross.append(svgFrom(closeSvg));

	// Unread indicator pinned to the bubble's corner. aria-hidden – the
	// unread count is carried by the button's aria-label so screen readers
	// hear one coherent sentence instead of a floating number.
	const badge = document.createElement("div");
	badge.className = "badge";
	badge.setAttribute("aria-hidden", "true");
	badge.dataset["state"] = "empty";

	if (config.mode === "bubble") {
		circle.append(logo, cross);
		button.append(glow, circle, badge);
	} else {
		// Tab modes: flat labeled tab pinned to an edge – no glow halo, no
		// close cross (the tab is fully hidden while the panel is open).
		const label = document.createElement("span");
		label.className = "tab-label";
		label.textContent = config.label;
		button.append(logo, label, badge);
	}
	button.addEventListener("click", onClick);

	shadow.append(button);
	if (config.mode === "bubble") {
		// Sibling after the button so `.fab:hover ~ .tooltip` in fab.css shows
		// it without JS hover tracking. aria-hidden – the button's aria-label
		// already carries the accessible name. The tab modes carry a visible
		// label instead.
		tooltip = document.createElement("div");
		tooltip.className = "tooltip";
		tooltip.setAttribute("aria-hidden", "true");
		tooltip.textContent = t(locale, "tooltip");
		shadow.append(tooltip);
	}
	document.body.append(host);

	const handleEnter = (): void => {
		onHoverChange?.(true);
	};
	const handleLeave = (): void => {
		onHoverChange?.(false);
	};
	// Keyboard focus counts as hover, so the onboarding card a pointer gets
	// on hover reaches keyboard users too. Focus events are composed, so they
	// reach the host from inside the closed shadow root.
	if (onHoverChange !== undefined) {
		host.addEventListener("mouseenter", handleEnter);
		host.addEventListener("mouseleave", handleLeave);
		host.addEventListener("focusin", handleEnter);
		host.addEventListener("focusout", handleLeave);
	}

	// Chat-head drag: every mode can float, each snapping back to its own home
	// edge on release (see fab-drag.ts).
	const placeListeners = new Set<(change: PlaceChange) => void>();
	const notifyPlaceChange = (change: PlaceChange): void => {
		for (const listener of placeListeners) {
			listener(change);
		}
	};
	const drag = config.draggable
		? attachChatHeadDrag(
				host,
				button,
				config.mode,
				config.marginPx,
				notifyPlaceChange,
			)
		: undefined;

	// A launcher still at its configured spot is pinned to the viewport edges
	// by CSS, so a resize moves it too. A parked one reports its own re-park.
	const onWindowResize = (): void => {
		if (drag?.restingRect() === undefined) {
			notifyPlaceChange("settled");
		}
	};
	window.addEventListener("resize", onWindowResize);

	// The bubble is fully hidden while the panel is open; closing plays a
	// "landing" animation where it drops in from the panel's top corner and
	// morphs back into the brand mark (see fab.css). The attribute is cleared
	// on a timer instead of `animationend` so it can't stick when
	// prefers-reduced-motion disables the animation.
	const LANDING_MS = 700;
	let landingTimer: ReturnType<typeof setTimeout> | undefined;
	let wasExpanded = false;
	let unreadCount = 0;

	// One coherent accessible name: "Open feedback widget – Unread
	// conversations: 3". The visual badge is aria-hidden.
	const openLabel = (): string =>
		unreadCount > 0
			? `${labelOpen} – ${tWith(currentLocale, "unread", {
					count: String(unreadCount),
				})}`
			: labelOpen;

	return {
		// The drag handler mirrors the parked side onto `data-position`, so the
		// attribute is the single source of truth for both CSS and this read.
		currentSide: () => (host.dataset["position"] === "left" ? "left" : "right"),
		dispose: () => {
			if (landingTimer !== undefined) {
				clearTimeout(landingTimer);
			}
			drag?.detach();
			window.removeEventListener("resize", onWindowResize);
			placeListeners.clear();
			button.removeEventListener("click", onClick);
			host.removeEventListener("mouseenter", handleEnter);
			host.removeEventListener("mouseleave", handleLeave);
			host.removeEventListener("focusin", handleEnter);
			host.removeEventListener("focusout", handleLeave);
			host.remove();
		},
		focus: () => {
			button.focus({ preventScroll: true });
		},
		onPlaceChange: (listener) => {
			placeListeners.add(listener);
			return () => {
				placeListeners.delete(listener);
			};
		},
		restingRect: () => {
			if (host.dataset["hidden"] === "true") {
				return undefined;
			}
			const parked = drag?.restingRect();
			if (parked !== undefined) {
				return parked;
			}
			const { bottom, left, right, top, width } = host.getBoundingClientRect();
			// Not laid out yet (detached, or `display: none` from a host rule).
			return width === 0 ? undefined : { bottom, left, right, top };
		},
		setBadge: (state) => {
			unreadCount = state.mode === "off" ? 0 : state.count;
			if (unreadCount <= 0) {
				badge.dataset["state"] = "empty";
				badge.textContent = "";
			} else if (state.mode === "dot") {
				badge.dataset["state"] = "dot";
				badge.textContent = "";
			} else {
				badge.dataset["state"] = "count";
				badge.textContent =
					state.count > BADGE_CAP ? `${BADGE_CAP}+` : String(state.count);
			}
			if (host.dataset["expanded"] !== "true") {
				button.setAttribute("aria-label", openLabel());
			}
		},
		setExpanded: (expanded) => {
			const value = expanded ? "true" : "false";
			host.dataset["expanded"] = value;
			button.setAttribute("aria-expanded", value);
			button.setAttribute("aria-label", expanded ? labelClose : openLabel());
			if (landingTimer !== undefined) {
				clearTimeout(landingTimer);
				landingTimer = undefined;
			}
			if (expanded) {
				// Reopening mid-landing cancels the flight.
				delete host.dataset["closing"];
			} else if (wasExpanded && config.mode === "bubble") {
				// The landing flight is a bubble affordance – tabs reappear in
				// place.
				host.dataset["closing"] = "true";
				landingTimer = setTimeout(() => {
					landingTimer = undefined;
					delete host.dataset["closing"];
				}, LANDING_MS);
			}
			wasExpanded = expanded;
		},
		setLocale: (next, refresh = false) => {
			if (next === currentLocale && !refresh) {
				return;
			}
			currentLocale = next;
			setLanguage(host, next);
			labelOpen = visibleLabel || t(next, "open");
			labelClose = t(next, "close");
			if (tooltip !== undefined) {
				tooltip.textContent = t(next, "tooltip");
			}
			button.setAttribute(
				"aria-label",
				host.dataset["expanded"] === "true" ? labelClose : openLabel(),
			);
		},
		setTooltipSuppressed: (suppressed) => {
			host.dataset["intro"] = suppressed ? "true" : "false";
		},
		setVisible: (next) => {
			const value = next ? "false" : "true";
			if (host.dataset["hidden"] === value) {
				return;
			}
			host.dataset["hidden"] = value;
			// A hidden launcher has no resting place to follow – its surfaces go
			// back to the corner, and return to it when it reappears.
			notifyPlaceChange("settled");
		},
	};
};
